/**
 * CP_CONTEXT - Injected by Succeed when course-planner-modern is embedded.
 * Provides API URL, district/school IDs, and user permissions.
 */

import { isSchoolIndividualCoursePlannerProductLevel } from './succeed-course-planner-product-level-cache';

declare global {
  interface Window {
    CP_CONTEXT?: {
      API_URL?: string;
      DISTRICT_ID?: string;
      HIGHSCHOOL_ID?: string;
      HS_DISTRICT_ID?: string;
      TOU_URI?: string;
      STUDENT_URL?: string;
      HELP_URL?: string;
      /**
       * Permission data from the host: numeric map (`Record<string, number>`) or slug list (`string[]`),
       * depending on embed. Legacy course-planner-ui staff typings use `string[]` here.
       */
      USER_PERMISSIONS?: Record<string, number> | string[];
      SUPER_USER?: string;
      /** Set by Succeed embed when consolidatedPermissions is ON (see micro-component/index.php). */
      CAN_VIEW_COURSE_CATALOG?: boolean;
      /** Set by Succeed embed when consolidatedPermissions is ON (see micro-component/index.php). */
      CAN_MANAGE_COURSE_CATALOG?: boolean;
      /** Set by Succeed embed when consolidatedPermissions is ON (see micro-component/index.php). */
      CAN_VIEW_PLANS_OF_STUDY?: boolean;
      /** Set by Succeed embed when consolidatedPermissions is ON (see micro-component/index.php). */
      CAN_MANAGE_PLANS_OF_STUDY?: boolean;
      /** Set by Succeed embed when consolidatedPermissions is ON (Configurations tab). */
      CAN_ADMINISTER_COURSE_PLANNER?: boolean;
      /** Set by Succeed embed when ManageandViewPermissionsUpdate is ON. */
      CAN_MANAGE_COURSE_PLANS?: boolean;
    };
    /**
     * JSON body from succeed-api GET /features (flat flag map). The host performs that request and assigns
     * the parsed object here; Course Planner does not call /features.
     */
    CP_COURSE_PLANNER_FEATURES?: Record<string, unknown>;
  }
}

const PLAN_APPROVAL_OPTIONS_LD_KEY = 'feature.coursePlanner.planApprovalOptions';
const CREDIT_RECOVERY_COURSES_LD_KEY = 'feature.coursePlanner.recoveryCourses.credits';
const RECOVERY_COURSES_LD_KEY = 'feature.coursePlanner.RecoveryCourses';
const RECOVERY_COURSE_FIX_FLAG_KEY = 'featureCoursePlannerRecoveryCourseFix';
/** Matches c-p-ui `isFeatureFlagAllowed` for course catalog export (async vs direct bulk export). */
const LIMIT_COURSES_FOR_LIST_VIEW_LD_KEY = 'feature.coursePlanner.limitCoursesForListView';
/** Epic NAVI-37940 — Neon-ized Student Course Plan Manager (SCPM). Gates the whole staff SCPM page/route. */
export const STUDENT_COURSE_PLAN_MANAGER_LD_KEY =
  'feature.coursePlanner.studentCoursePlanManager' as const;
/** LD flag gating student course recommendations independently from the SCPM page. */
export const STUDENT_COURSE_RECOMMENDATIONS_LD_KEY =
  'feature.coursePlanner.studentCourseRecommendations' as const;
/** Release gate for partial attempted-credit metadata on In Progress course cards. */
export const SCPM_GRID_ITEM_HOST_KEY = 'releaseSharedCoursePlannerScpmGridItem' as const;
/** Epic NAVI-37948 — consolidated staff permissions (catalog, POS, etc.; not manage/view course plans). */
export const CONSOLIDATED_PERMISSIONS_LD_KEY =
  'feature.coursePlanner.consolidatedPermissions' as const;
/** camelCase key on FeatureFlagsManager / succeed GET /features (check both). */
export const CONSOLIDATED_PERMISSIONS_LD_KEY_CAMEL =
  'featureCoursePlannerConsolidatedPermissions' as const;

function readLdBoolean(map: Record<string, unknown> | undefined, key: string): boolean | undefined {
  if (!map) {
    return undefined;
  }
  const raw = map[key];
  if (typeof raw === 'boolean') {
    return raw;
  }
  if (typeof raw === 'number') {
    return raw !== 0;
  }
  if (typeof raw === 'string') {
    const s = raw.toLowerCase();
    if (s === 'true') {
      return true;
    }
    if (s === 'false') {
      return false;
    }
  }
  return undefined;
}

function getCoursePlannerFeatureMap(): Record<string, unknown> | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window.CP_STUDENT_CONTEXT?.CP_COURSE_PLANNER_FEATURES ?? window.CP_COURSE_PLANNER_FEATURES;
}

/**
 * Checks the release gate for the updated student course-grid item display.
 * Reads the camelCase key supplied by the student host.
 *
 * @returns The host flag value, defaulting to false when the flag is absent.
 */
export function isScpmGridItemEnabled(): boolean {
  const features = getCoursePlannerFeatureMap();
  return readLdBoolean(features, SCPM_GRID_ITEM_HOST_KEY) ?? false;
}

/**
 * Access control role names used by user session payloads.
 */
export enum AclRoles {
  GUEST = 'GUEST',
  STUDENT = 'STUDENT',
  PARENT = 'PARENT',
  COUNSELOR = 'COUNSELOR',
  DISTRICT_USER = 'DISTRICT_USER',
  SCHOOL_USER = 'SCHOOL_USER',
  SUPER_USER = 'SUPER_USER',
  ZONE_USER = 'ZONE_USER',
}

export interface LoginUser {
  userId: number;
  role: string | AclRoles;
  permissions?: Record<string, unknown>;
}

/**
 * User session shape used by CP permission and ghosting helpers.
 */
export interface UserSession {
  id?: number;
  username?: string;
  role: string | AclRoles;
  highSchoolName?: string;
  highSchoolAlias?: string;
  highSchoolId?: string;
  districtAlias?: string;
  districtId?: string;
  state?: string;
  grade?: number;
  email?: string;
  fullName?: string;
  classYear: number;
  isDemo?: boolean;
  /** Present only for staff ghosting/impersonating another user; absent for a direct student login. */
  loginUser?: LoginUser;
  /** From BRAPI `GET /security/permissions` (`resources.courses` etc. — attached for direct student logins). */
  permissions?: {
    resources: { [key: string]: number | string[] };
  };
  /** From BRAPI expanded permissions (`coursePlannerStaffInStudentFolder`). */
  coursePlannerStaffInStudentFolder?: boolean;
  parent?: { id?: number; role?: string };
  staffUser?: { id?: number; role?: string };
}

function hasLoginUserField(user: unknown): user is Pick<UserSession, 'loginUser'> {
  return typeof user === 'object' && user !== null && 'loginUser' in user;
}

/**
 * Returns true when the current user session is a ghosted session.
 *
 * @param user - User session object (or null/undefined)
 * @returns True when `loginUser` is present
 */
export const isGhosted = (user: unknown): boolean => {
  if (!hasLoginUserField(user)) {
    return false;
  }
  return Boolean(user.loginUser);
};

export const isStudent = (user: UserSession | null | undefined): boolean => {
  return user?.role === AclRoles.STUDENT;
};

export const isDemo = (user: UserSession | null | undefined): boolean => {
  return Boolean(user?.isDemo);
};

/** Priority: Edit screen – when present, user can edit course plans. */
export const PERMISSION_ALIAS_MANAGE_COURSE_PLANS = 'edit_student_course_plans';
/** Priority: View screen – when present (and no edit), view-only. */
export const PERMISSION_ALIAS_VIEW_COURSE_PLANS = 'view_student_course_plans';
/**
 * Base "Course Planner Next" product alias — always present alongside the manage/view aliases on
 * BRAPI `GET /security/permissions` `resources.courses`. For a **direct student login** (not staff
 * ghosting), this is required but NOT sufficient on its own — see
 * {@link PERMISSION_ALIAS_VIEW_AND_EDIT_COURSE_PLANS}; both aliases must be present together to gate
 * Manage Course Plans access (see {@link getCoursePlanAccessState}).
 */
export const PERMISSION_ALIAS_COURSE_PLANNER_NEXT = 'course_planner_next';
/**
 * Legacy combined course-plan alias. Only applicable to a **direct student login** gate (see
 * {@link getCoursePlanAccessState}) — does NOT apply to ghosted staff sessions.
 */
export const PERMISSION_ALIAS_VIEW_AND_EDIT_COURSE_PLANS = 'view_and_edit_course_plans';

/**
 * Legacy numeric bits for `loginUser.permissions.resources.courses` when the host delivers the
 * old numeric bitmask instead of the newer BRAPI string-alias array (seen on some ghosted-staff
 * sessions, e.g. district-level ghosting). 128 = Manage, 256 = View.
 */
const LEGACY_NUMERIC_MANAGE_COURSE_PLANS = 128;
const LEGACY_NUMERIC_VIEW_COURSE_PLANS = 256;

/** Raw (unfiltered) `loginUser.permissions.resources.courses` value — may be a number, number[], or string[]. */
function getLoginUserCoursesRaw(user: UserSession | null | undefined): unknown {
  const perms = user?.loginUser?.permissions;
  return perms && typeof perms === 'object' && 'resources' in perms
    ? (perms as { resources?: { courses?: unknown } }).resources?.courses
    : undefined;
}

/**
 * True when the legacy numeric `courses` value grants `bit`. A scalar value is a bitmask (e.g. `384`
 * = `128 | 256` for combined Manage + View), tested with a bitwise AND — not equality, otherwise a
 * user with multiple bits set would match neither and be denied entirely. A `number[]` is the older
 * discrete-permission-id list format and is still tested by exact membership.
 */
function hasLegacyNumericCoursePermission(courses: unknown, bit: number): boolean {
  if (typeof courses === 'number') {
    return (courses & bit) === bit;
  }
  return (
    Array.isArray(courses) &&
    courses.every((permission) => typeof permission === 'number') &&
    courses.includes(bit)
  );
}

/** Extracts a `resources.courses` alias list from a permissions object, or a flat string array. */
function extractCoursePlanAliasesFromPermissions(perms: unknown): string[] {
  if (!perms) {
    return [];
  }

  const resourcesCourses =
    typeof perms === 'object' && perms !== null && 'resources' in perms
      ? (perms as { resources?: { courses?: unknown } }).resources?.courses
      : undefined;

  if (Array.isArray(resourcesCourses)) {
    return resourcesCourses.filter(
      (permission): permission is string => typeof permission === 'string'
    );
  }

  if (Array.isArray(perms) && perms.every((permission) => typeof permission === 'string')) {
    return perms;
  }

  return [];
}

/**
 * Resolve courses permission aliases. When `loginUser` is present (staff ghosting a student), only
 * `loginUser.permissions` is consulted — even an empty alias list there is authoritative and must
 * NOT fall back to the real student's top-level `permissions` (that would let a ghosting staff actor
 * inherit the student's own aliases, e.g. `edit_student_course_plans`, granting unintended edit access).
 * Only a session with no `loginUser` at all (direct student login) reads top-level `permissions`
 * (BRAPI `GET /security/permissions` response attached to the host session).
 * Supports nested `resources.courses` arrays and flat string arrays.
 */
function getCoursePlanPermissionAliases(user: UserSession | null | undefined): string[] {
  if (user?.loginUser) {
    return extractCoursePlanAliasesFromPermissions(user.loginUser.permissions);
  }
  return extractCoursePlanAliasesFromPermissions(user?.permissions);
}

/**
 * True if the user can add/edit course plans (Manage).
 * Students always get edit; permission checks apply to staff context.
 */
export const canEditPlan = (
  user: UserSession | null | undefined,
  useNewPermissionsModel: boolean
): boolean => {
  if (isDemo(user)) {
    return true;
  }
  if (
    isStudentCoursePlanManagerEnabled(true) &&
    getEffectiveRoleFromSession(user) === AclRoles.SUPER_USER
  ) {
    return true;
  }
  // Real student login (no staff context): always allow edit. When staff view a student plan they
  // have loginUser set (isGhosted); permissions logic below applies to them.
  if (isStudent(user) && !isGhosted(user)) {
    return true;
  }
  if (user?.loginUser === null || user?.loginUser === undefined) {
    return false;
  }

  const courses = getLoginUserCoursesRaw(user);
  const aliases = getCoursePlanPermissionAliases(user);

  // Legacy numeric: 128 = manage (always)
  if (hasLegacyNumericCoursePermission(courses, LEGACY_NUMERIC_MANAGE_COURSE_PLANS)) {
    return true;
  }

  const hasManage = aliases.includes(PERMISSION_ALIAS_MANAGE_COURSE_PLANS);

  if (useNewPermissionsModel) {
    // Flag ON: Manage -> edit; View present + Manage absent -> view-only; neither -> deny.
    return hasManage;
  }

  // Flag OFF: the granular Manage/View aliases are not honored at all; no legacy fallback either.
  return false;
};

/**
 * LD flag for Configurations → Student Submission Rules (`feature.coursePlanner.planApprovalOptions`).
 * Reads from the active course-planner feature map first — student MFE uses
 * `CP_STUDENT_CONTEXT.CP_COURSE_PLANNER_FEATURES`, while staff/global embeds may use
 * `window.CP_COURSE_PLANNER_FEATURES`.
 * (same keys as e.g. succeed-api …/features). Falls back to `window[flagKey]` for local/tests.
 * Course Planner never requests /features; only the host does.
 * Embedded but flag missing → false. Local standalone with no value → true (Approvals tab testable).
 */
export function isCoursePlannerPlanApprovalOptionsEnabled(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }
  const fromFeaturesBody = readLdBoolean(
    getCoursePlannerFeatureMap(),
    PLAN_APPROVAL_OPTIONS_LD_KEY
  );
  if (fromFeaturesBody !== undefined) {
    return fromFeaturesBody;
  }
  const w = window as unknown as Record<string, unknown>;
  const fromWindowKey = readLdBoolean(
    { [PLAN_APPROVAL_OPTIONS_LD_KEY]: w[PLAN_APPROVAL_OPTIONS_LD_KEY] },
    PLAN_APPROVAL_OPTIONS_LD_KEY
  );
  if (fromWindowKey !== undefined) {
    return fromWindowKey;
  }
  return !isEmbeddedInSucceed();
}

/**
 * LD flag gating the Neon-ized Student Course Plan Manager (SCPM) staff page (epic NAVI-37940).
 * When OFF, the SCPM route/components must not render (legacy `course-planner-ui` page is used instead,
 * outside this repo). Same read precedence as {@link isCoursePlannerPlanApprovalOptionsEnabled}.
 * Embedded but flag missing → false (do not show unfinished work in production). Local standalone with
 * no value → true (page testable in dev).
 * Pass requireExplicitFlag for the new approval workflow: missing values stay OFF even locally.
 */
export function isStudentCoursePlanManagerEnabled(requireExplicitFlag = false): boolean {
  if (typeof window === 'undefined') {
    return !requireExplicitFlag;
  }
  const features = getCoursePlannerFeatureMap();
  const fromFeaturesBody =
    readLdBoolean(features, STUDENT_COURSE_PLAN_MANAGER_LD_KEY) ??
    readLdBoolean(features, 'featureCoursePlannerStudentCoursePlanManager');
  if (requireExplicitFlag && features !== undefined) {
    return fromFeaturesBody === true;
  }
  if (fromFeaturesBody !== undefined) {
    return fromFeaturesBody;
  }
  const w = window as unknown as Record<string, unknown>;
  const fromWindowKey = readLdBoolean(
    { [STUDENT_COURSE_PLAN_MANAGER_LD_KEY]: w[STUDENT_COURSE_PLAN_MANAGER_LD_KEY] },
    STUDENT_COURSE_PLAN_MANAGER_LD_KEY
  );
  if (fromWindowKey !== undefined) {
    return fromWindowKey;
  }
  return !requireExplicitFlag && !isEmbeddedInSucceed();
}

/**
 * LD flag gating student course recommendations.
 * Embedded but flag missing → false. Local standalone with no value → true (page testable in dev).
 */
export function isStudentCourseRecommendationsEnabled(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }
  const fromFeaturesBody = readLdBoolean(
    getCoursePlannerFeatureMap(),
    STUDENT_COURSE_RECOMMENDATIONS_LD_KEY
  );
  if (fromFeaturesBody !== undefined) {
    return fromFeaturesBody;
  }
  const w = window as unknown as Record<string, unknown>;
  const fromWindowKey = readLdBoolean(
    { [STUDENT_COURSE_RECOMMENDATIONS_LD_KEY]: w[STUDENT_COURSE_RECOMMENDATIONS_LD_KEY] },
    STUDENT_COURSE_RECOMMENDATIONS_LD_KEY
  );
  if (fromWindowKey !== undefined) {
    return fromWindowKey;
  }
  return !isEmbeddedInSucceed();
}

/**
 * LD flag for Credit recovery + Equivalent courses on the add/edit course form
 * (`feature.coursePlanner.recoveryCourses.credits`), matching legacy course-planner-ui.
 * Embedded without the flag → false. Local standalone without the flag → true (sections testable).
 */
export function isCreditRecoveryCoursesFeatureEnabled(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }
  const fromFeaturesBody = readLdBoolean(
    getCoursePlannerFeatureMap(),
    CREDIT_RECOVERY_COURSES_LD_KEY
  );
  if (fromFeaturesBody !== undefined) {
    return fromFeaturesBody;
  }
  const w = window as unknown as Record<string, unknown>;
  const fromWindowKey = readLdBoolean(
    { [CREDIT_RECOVERY_COURSES_LD_KEY]: w[CREDIT_RECOVERY_COURSES_LD_KEY] },
    CREDIT_RECOVERY_COURSES_LD_KEY
  );
  if (fromWindowKey !== undefined) {
    return fromWindowKey;
  }
  return !isEmbeddedInSucceed();
}

/**
 * LD flag for Recovery Courses behavior (`feature.coursePlanner.RecoveryCourses`).
 * Reads from the active course-planner feature map first, then `window[flagKey]` fallback.
 * Missing/undefined is treated as disabled so behavior stays behind an explicit flag gate.
 */
export function isRecoveryCoursesFeatureEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const fromFeaturesBody = readLdBoolean(getCoursePlannerFeatureMap(), RECOVERY_COURSES_LD_KEY);
  if (fromFeaturesBody !== undefined) {
    return fromFeaturesBody;
  }
  const w = window as unknown as Record<string, unknown>;
  const fromWindowKey = readLdBoolean(
    { [RECOVERY_COURSES_LD_KEY]: w[RECOVERY_COURSES_LD_KEY] },
    RECOVERY_COURSES_LD_KEY
  );
  if (fromWindowKey !== undefined) {
    return fromWindowKey;
  }
  return false;
}

/**
 * LD flag gating the inferred remaining-credit recovery fix.
 * Missing/undefined is disabled so the existing plan-grid behavior remains unchanged by default.
 */
export function isRecoveryCourseFixEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const fromFeaturesBody = readLdBoolean(
    getCoursePlannerFeatureMap(),
    RECOVERY_COURSE_FIX_FLAG_KEY
  );
  if (fromFeaturesBody !== undefined) {
    return fromFeaturesBody;
  }
  const w = window as unknown as Record<string, unknown>;
  const fromWindowKey = readLdBoolean(
    { [RECOVERY_COURSE_FIX_FLAG_KEY]: w[RECOVERY_COURSE_FIX_FLAG_KEY] },
    RECOVERY_COURSE_FIX_FLAG_KEY
  );
  return fromWindowKey ?? false;
}

/**
 * When true (embedded Succeed only), **district** course catalog export uses the legacy async pipeline:
 * GET `/data-export/exportCoursesAsync/exportCourseCatalogUI` → poll → download.
 * **School** staff catalog export uses `GET /course-planner/courses/bulk/exportSchool` instead (scoped on c-p-s).
 * **Course mapping** export uses direct `POST .../bulk/export-course-mapping-V1` with `{ headers, schools }`
 * (async mapping jobs do not accept schools). When false, catalog uses direct `GET .../bulk/export`.
 * Local dev is never embedded, so catalog is always direct GET.
 *
 * Semantics match c-p-ui `isFeatureFlagAllowed(features, 'feature.coursePlanner.limitCoursesForListView')`:
 * `undefined` or `true` → async; only explicit `false` → direct.
 */
export function isCourseCatalogExportAsyncEnabled(): boolean {
  if (typeof window === 'undefined' || !isEmbeddedInSucceed()) {
    return false;
  }
  const fromFeaturesBody = readLdBoolean(
    window.CP_COURSE_PLANNER_FEATURES,
    LIMIT_COURSES_FOR_LIST_VIEW_LD_KEY
  );
  if (fromFeaturesBody === false) {
    return false;
  }
  if (fromFeaturesBody === true) {
    return true;
  }
  const w = window as unknown as Record<string, unknown>;
  const fromWindowKey = readLdBoolean(
    { [LIMIT_COURSES_FOR_LIST_VIEW_LD_KEY]: w[LIMIT_COURSES_FOR_LIST_VIEW_LD_KEY] },
    LIMIT_COURSES_FOR_LIST_VIEW_LD_KEY
  );
  if (fromWindowKey === false) {
    return false;
  }
  if (fromWindowKey === true) {
    return true;
  }
  // Missing flag in embedded mode → same as c-p-ui (treat as allowed / async).
  return true;
}

/**
 * Returns true when the app is embedded in Succeed (CP_CONTEXT is present).
 * This is staff-side only. Student-side (FCUI) uses CP_STUDENT_CONTEXT — see
 * {@link isEmbeddedInHost} for a check that covers both.
 */
export function isEmbeddedInSucceed(): boolean {
  return typeof window !== 'undefined' && !!window.CP_CONTEXT;
}

/**
 * Returns true when embedded in any host — Succeed (staff) or FCUI (student).
 * Use this for logic that applies to both contexts, e.g. proxied API path selection.
 */
export function isEmbeddedInHost(): boolean {
  return typeof window !== 'undefined' && (!!window.CP_CONTEXT || !!window.CP_STUDENT_CONTEXT);
}

/**
 * Returns true when embedded in FCUI (student host). CP_STUDENT_CONTEXT is present
 * but CP_CONTEXT is not (that's the staff/Succeed context).
 * Use to gate staff-only API calls (e.g. config) that don't exist on the student host.
 */
export function isEmbeddedInFCUI(): boolean {
  return typeof window !== 'undefined' && !!window.CP_STUDENT_CONTEXT && !window.CP_CONTEXT;
}

/** NAVI-40270: gates “Schedule for later” in Publish and update drawer (Plan of study). */
const PUBLISH_SCHEDULE_LD_KEY = 'feature.shared.coursePlanner.publishSchedule';
const TERM_SETTINGS_LD_KEY = 'release.coursePlanner.termSettings';
const SCHOOL_SELECTION_LD_KEY = 'release.coursePlanner.schoolSelection';

/** WAF-safe Base64 wrapping for rich-text descriptions on save (must match CPS decode flag). */
const COURSE_PLANNER_MODERN_WAF_LD_KEY = 'release.shared.coursePlanner.coursePlannerModern';

function isCoursePlannerModernWafDescriptionEncodingEnabled(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }
  const fromFeaturesBody = readLdBoolean(
    window.CP_COURSE_PLANNER_FEATURES,
    COURSE_PLANNER_MODERN_WAF_LD_KEY
  );
  if (fromFeaturesBody !== undefined) {
    return fromFeaturesBody;
  }
  const w = window as unknown as Record<string, unknown>;
  const fromWindowKey = readLdBoolean(
    { [COURSE_PLANNER_MODERN_WAF_LD_KEY]: w[COURSE_PLANNER_MODERN_WAF_LD_KEY] },
    COURSE_PLANNER_MODERN_WAF_LD_KEY
  );
  if (fromWindowKey !== undefined) {
    return fromWindowKey;
  }
  return !isEmbeddedInSucceed();
}

/**
 * When true, Plan of study **Publish and update** shows “Schedule for later” (date/time) and **Schedule** CTA.
 * When false, staff go straight to school selection for publish (publish-now only).
 * Reads `CP_COURSE_PLANNER_FEATURES` from the host. Embedded with no value → false; local standalone → true.
 */
export function isPublishScheduleFeatureEnabled(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }
  const fromFeaturesBody = readLdBoolean(
    window.CP_COURSE_PLANNER_FEATURES,
    PUBLISH_SCHEDULE_LD_KEY
  );
  if (fromFeaturesBody !== undefined) {
    return fromFeaturesBody;
  }
  const w = window as unknown as Record<string, unknown>;
  const fromWindowKey = readLdBoolean(
    { [PUBLISH_SCHEDULE_LD_KEY]: w[PUBLISH_SCHEDULE_LD_KEY] },
    PUBLISH_SCHEDULE_LD_KEY
  );
  if (fromWindowKey !== undefined) {
    return fromWindowKey;
  }
  return !isEmbeddedInSucceed();
}

/**
 * Controls Configurations -> Student experience -> Term settings.
 * Embedded without an explicit flag value -> false.
 * Local standalone without an explicit flag value -> true.
 */
export function isTermSettingsFeatureEnabled(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }
  const fromFeaturesBody = readLdBoolean(window.CP_COURSE_PLANNER_FEATURES, TERM_SETTINGS_LD_KEY);
  if (fromFeaturesBody !== undefined) {
    return fromFeaturesBody;
  }
  const w = window as unknown as Record<string, unknown>;
  const fromWindowKey = readLdBoolean(
    { [TERM_SETTINGS_LD_KEY]: w[TERM_SETTINGS_LD_KEY] },
    TERM_SETTINGS_LD_KEY
  );
  if (fromWindowKey !== undefined) {
    return fromWindowKey;
  }
  return !isEmbeddedInSucceed();
}

/**
 * Controls Configurations -> Student experience -> School selection.
 * Embedded without an explicit flag value -> false.
 * Local standalone without an explicit flag value -> true.
 */
export function isSchoolSelectionFeatureEnabled(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }
  const fromFeaturesBody = readLdBoolean(
    window.CP_COURSE_PLANNER_FEATURES,
    SCHOOL_SELECTION_LD_KEY
  );
  if (fromFeaturesBody !== undefined) {
    return fromFeaturesBody;
  }
  const w = window as unknown as Record<string, unknown>;
  const fromWindowKey = readLdBoolean(
    { [SCHOOL_SELECTION_LD_KEY]: w[SCHOOL_SELECTION_LD_KEY] },
    SCHOOL_SELECTION_LD_KEY
  );
  if (fromWindowKey !== undefined) {
    return fromWindowKey;
  }
  return !isEmbeddedInSucceed();
}

/**
 * When true, plan of study create/update/publish bodies encode `description` fields for WAF transport.
 * Reads `CP_COURSE_PLANNER_FEATURES` from the host. Embedded with no value → false; local standalone → true.
 */
export function isPlanOfStudyWafDescriptionEncodingEnabled(): boolean {
  return isCoursePlannerModernWafDescriptionEncodingEnabled();
}

/**
 * When true, course catalog create/update encodes `course.description` for WAF transport.
 * Same semantics as {@link isPlanOfStudyWafDescriptionEncodingEnabled}.
 */
export function isCourseCatalogWafDescriptionEncodingEnabled(): boolean {
  return isCoursePlannerModernWafDescriptionEncodingEnabled();
}

/**
 * When true, cluster / pathway create/update/publish bodies encode `description` fields for WAF transport.
 * Same semantics as {@link isPlanOfStudyWafDescriptionEncodingEnabled}.
 */
export function isClustersAndPathwaysWafDescriptionEncodingEnabled(): boolean {
  return isCoursePlannerModernWafDescriptionEncodingEnabled();
}

/**
 * Local dev: Vite + VITE_QA_SUCCEED_API and a proxy to QA succeed-api (see dev-course-planner-qa-backend.sh).
 * Behaves like embedded for API paths and succeed-only endpoints, without CP_CONTEXT.
 */
export function isQaSucceedApiProxyDev(): boolean {
  return Boolean(import.meta.env.DEV && import.meta.env.VITE_QA_SUCCEED_API === 'true');
}

/**
 * Returns the API base URL. Uses CP_CONTEXT when embedded, else VITE_API_URL or /api.
 */
export function getApiBaseUrl(): string {
  if (typeof window !== 'undefined' && window.CP_STUDENT_CONTEXT?.API_URL) {
    return window.CP_STUDENT_CONTEXT.API_URL;
  }
  if (typeof window !== 'undefined' && window.CP_CONTEXT?.API_URL) {
    return window.CP_CONTEXT.API_URL;
  }
  // QA proxy dev builds paths without an /api prefix (see api-paths.ts); avoid
  // double-prefixing so requests match the vite QA proxy rules instead of the
  // default /api -> localhost:3003 rule.
  if (isQaSucceedApiProxyDev()) {
    return '';
  }
  return import.meta.env.VITE_API_URL || '/api';
}

const IMPORT_COURSES_PATH = '/powerschool-data-integration/course-catalog';

/**
 * Returns the full URL for the "Import courses" page so it follows the current environment.
 * When embedded in Succeed (QA or prod), uses the current origin so the link stays on the same host.
 * Otherwise uses VITE_SUCCEED_URL if set, then falls back to production Succeed.
 */
export function getImportCoursesUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin && isEmbeddedInSucceed()) {
    return `${window.location.origin}${IMPORT_COURSES_PATH}`;
  }
  const base = import.meta.env.VITE_SUCCEED_URL;
  if (base) {
    return `${base.replace(/\/$/, '')}${IMPORT_COURSES_PATH}`;
  }
  return `https://succeed.naviance.com${IMPORT_COURSES_PATH}`;
}

const CUSTOMIZABLE_DASHBOARD_PATH = '/customizable-dashboard/';

/** Succeed access-denied entry (redirects to auth/accessdenied). Matches micro-component and student-course-common. */
const ACCESS_DENIED_PATH = '/accessdenied.php';

/** FCUI's own access-denied page (see family-connection-ui's containers/Errors -> 403 `Denied`). */
const FCUI_ACCESS_DENIED_PATH = '/error/access-denied';

/**
 * Full URL for Succeed access denied when the user hits a route they are not permitted to use.
 * Staff (Succeed) embed: host origin + `/accessdenied.php`. Student (FCUI) embed:
 * `/error/access-denied` — FCUI's own access-denied page (`/staff` is a staff-only path and does
 * not exist on the student host; domain root previously sent denied users straight to the student
 * dashboard instead of an access-denied message). Local dev (no embed at all): in-app staff
 * landing (`/staff`).
 */
export function getAccessDeniedUrl(): string {
  if (isEmbeddedInFCUI()) {
    return FCUI_ACCESS_DENIED_PATH;
  }
  if (!isEmbeddedInSucceed()) {
    return '/staff';
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${ACCESS_DENIED_PATH}`;
  }
  const base = import.meta.env.VITE_SUCCEED_URL?.replace(/\/$/, '');
  if (base) {
    return `${base}${ACCESS_DENIED_PATH}`;
  }
  return `https://succeed.naviance.com${ACCESS_DENIED_PATH}`;
}

/** Full-page redirect to {@link getAccessDeniedUrl} (required outside the MFE router basename). */
export function redirectToAccessDenied(): void {
  if (typeof window !== 'undefined') {
    window.location.assign(getAccessDeniedUrl());
  }
}

/**
 * Returns the staff Home URL for UI navigation.
 * Embedded mode uses the Succeed staff dashboard; local mode uses the staff dashboard route.
 */
export function getHomeUrl(): string {
  if (!isEmbeddedInSucceed()) {
    return '/staff/dashboard';
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${CUSTOMIZABLE_DASHBOARD_PATH}`;
  }
  const base = import.meta.env.VITE_SUCCEED_URL?.replace(/\/$/, '');
  if (base) {
    return `${base}${CUSTOMIZABLE_DASHBOARD_PATH}`;
  }
  return `https://succeed.naviance.com${CUSTOMIZABLE_DASHBOARD_PATH}`;
}

const STAFF_BASENAME = '/course-planner-next';

/**
 * Full URL to the Course catalog staff page (embed-aware).
 * Embedded: current origin + /course-planner-next/courses/course-catalog.
 * Local: in-app path /courses/course-catalog.
 */
export function getCourseCatalogListUrl(): string {
  const path = `${STAFF_BASENAME}/courses/course-catalog`;
  if (typeof window !== 'undefined' && window.location?.origin && isEmbeddedInSucceed()) {
    return `${window.location.origin}${path}`;
  }
  if (!isEmbeddedInSucceed()) {
    return '/courses/course-catalog';
  }
  const base = import.meta.env.VITE_SUCCEED_URL?.replace(/\/$/, '');
  if (base) {
    return `${base}${path}`;
  }
  return `https://succeed.naviance.com${path}`;
}

/**
 * Returns the full path for a staff route when used as href (e.g. in breadcrumbs).
 * When embedded in Succeed, prepends /course-planner-next so links navigate correctly.
 */
export function getStaffRoutePath(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return isEmbeddedInSucceed() ? `${STAFF_BASENAME}${normalized}` : normalized;
}

/**
 * Returns the district ID. Uses CP_CONTEXT when embedded, else VITE_DISTRICT_ID or fallback.
 */
export function getDistrictId(): string {
  if (typeof window !== 'undefined' && window.CP_STUDENT_CONTEXT?.DISTRICT_ID) {
    return window.CP_STUDENT_CONTEXT.DISTRICT_ID;
  }
  if (typeof window !== 'undefined' && window.CP_CONTEXT) {
    const ctx = window.CP_CONTEXT;
    const id = ctx.DISTRICT_ID ?? ctx.HS_DISTRICT_ID;
    if (id) {
      return id;
    }
  }
  return import.meta.env.VITE_DISTRICT_ID || '9000001DUS';
}

/**
 * Same AND-group semantics as `hasPermissions` in course-planner-ui `userSession.selectors.ts`.
 */
function hasEveryPermissionSlug(
  permissions: readonly string[],
  required: readonly string[]
): boolean {
  return required.every((perm) => permissions.includes(perm));
}

/** OR of AND groups — legacy succeed / course-planner-ui `hasPermissions` semantics. */
export function hasAnyPermissionGroup(
  slugs: readonly string[],
  groups: readonly (readonly string[])[]
): boolean {
  return groups.some((required) => hasEveryPermissionSlug(slugs, required));
}

/** Slug list from Succeed embed (`CP_CONTEXT.USER_PERMISSIONS` as string[]); empty if numeric map only. */
export function getCpContextPermissionSlugs(): readonly string[] {
  const raw = typeof window !== 'undefined' ? window.CP_CONTEXT?.USER_PERMISSIONS : undefined;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((p): p is string => typeof p === 'string');
}

export type CpStaffPermissionAllowedOptions = {
  consolidatedEnabled: boolean;
  newAliases: readonly string[];
  legacyGroups: readonly (readonly string[])[];
  slugs?: readonly string[];
};

/**
 * Consolidated permission check for staff CP Next (NAVI-37948).
 * - flag OFF → any matching legacyGroups grants access
 * - flag ON → all newAliases must be present
 * When slugs are empty (numeric USER_PERMISSIONS), returns false; callers use legacy fallbacks (e.g. isSchoolUser).
 */
export function cpStaffPermissionAllowed(options: CpStaffPermissionAllowedOptions): boolean {
  const slugs = options.slugs ?? getCpContextPermissionSlugs();
  if (slugs.length === 0) {
    return false;
  }
  if (options.consolidatedEnabled) {
    return hasEveryPermissionSlug(slugs, options.newAliases);
  }
  return hasAnyPermissionGroup(slugs, options.legacyGroups);
}

/**
 * True when `feature.coursePlanner.consolidatedPermissions` is on in the active feature map
 * (or camelCase equivalent). Missing or unset → false (legacy permission behavior).
 */
export function isConsolidatedPermissionsEnabled(features?: Record<string, unknown>): boolean {
  const map = features ?? getCoursePlannerFeatureMap();
  return (
    readLdBoolean(map, CONSOLIDATED_PERMISSIONS_LD_KEY) === true ||
    readLdBoolean(map, CONSOLIDATED_PERMISSIONS_LD_KEY_CAMEL) === true
  );
}

/** NAVI-40136 — canonical view course history alias when consolidatedPermissions is ON. */
export const PERMISSION_ALIAS_VIEW_COURSE_HISTORY = 'view_student_course_history' as const;

/** @deprecated NAVI-40136 — district legacy alias; flag OFF dual-read only. */
export const PERMISSION_ALIAS_VIEW_SCHOOL_COURSE_HISTORY =
  'view_school_student_course_history' as const;

/**
 * NAVI-40136 — student folder view course history.
 * Flag ON: view_student_course_history only.
 * Flag OFF: either legacy alias (matches succeed OR / product-split fallbacks).
 */
export function canViewCourseHistoryFromCpContext(
  consolidatedEnabled?: boolean,
  slugs?: readonly string[]
): boolean {
  const permissionSlugs = slugs ?? getCpContextPermissionSlugs();
  if (permissionSlugs.length === 0) {
    return false;
  }
  const consolidated = consolidatedEnabled ?? isConsolidatedPermissionsEnabled();
  if (consolidated) {
    return permissionSlugs.includes(PERMISSION_ALIAS_VIEW_COURSE_HISTORY);
  }
  return (
    permissionSlugs.includes(PERMISSION_ALIAS_VIEW_COURSE_HISTORY) ||
    permissionSlugs.includes(PERMISSION_ALIAS_VIEW_SCHOOL_COURSE_HISTORY)
  );
}

/** NAVI-40131/40132 — canonical manage course catalog alias when consolidatedPermissions is ON. */
export const PERMISSION_ALIAS_MANAGE_COURSE_CATALOG = 'admin_course_catalog' as const;

/** NAVI-40131/40132 — canonical view course catalog alias when consolidatedPermissions is ON. */
export const PERMISSION_ALIAS_VIEW_COURSE_CATALOG = 'view_courses_tab' as const;

function readCpContextBoolean(
  key:
    | 'CAN_VIEW_COURSE_CATALOG'
    | 'CAN_MANAGE_COURSE_CATALOG'
    | 'CAN_VIEW_PLANS_OF_STUDY'
    | 'CAN_MANAGE_PLANS_OF_STUDY'
    | 'CAN_ADMINISTER_COURSE_PLANNER'
    | 'CAN_MANAGE_COURSE_PLANS'
): boolean | undefined {
  if (typeof window === 'undefined' || !window.CP_CONTEXT) {
    return undefined;
  }
  const value = window.CP_CONTEXT[key];
  if (value === true || value === false) {
    return value;
  }
  return undefined;
}

/**
 * NAVI-40131/40132 — view course catalog (route gate / nav).
 * Flag ON: host boolean from Succeed when embedded; else slug aliases
 * (view_courses_tab or admin_course_catalog; manage implies view).
 * Flag OFF: legacy alias by embed scope; when slugs are absent, allow (host parity).
 */
export function canViewCourseCatalogFromCpContext(
  consolidatedEnabled?: boolean,
  slugs?: readonly string[]
): boolean {
  const permissionSlugs = slugs ?? getCpContextPermissionSlugs();
  const consolidated = consolidatedEnabled ?? isConsolidatedPermissionsEnabled();

  if (consolidated) {
    const hostView = readCpContextBoolean('CAN_VIEW_COURSE_CATALOG');
    if (hostView !== undefined) {
      return hostView;
    }
    if (permissionSlugs.length === 0) {
      return false;
    }
    return (
      permissionSlugs.includes(PERMISSION_ALIAS_VIEW_COURSE_CATALOG) ||
      permissionSlugs.includes(PERMISSION_ALIAS_MANAGE_COURSE_CATALOG)
    );
  }

  if (permissionSlugs.length === 0) {
    return true;
  }

  if (isSchoolScopedByCpContext()) {
    return permissionSlugs.includes('view_school_course_catalog');
  }

  return (
    permissionSlugs.includes('view_district_course_catalog') ||
    permissionSlugs.includes('view_school_course_catalog')
  );
}

/**
 * NAVI-40131 — effective manage course catalog capability (mutations / manage UI).
 * Flag ON: `admin_course_catalog` plus the same school-scope / SCHOOL_INDIVIDUAL rules as
 * {@link isCourseCatalogReadOnly}. Flag OFF: legacy manage aliases when not {@link isSchoolUser}.
 */
export function canManageCourseCatalogFromCpContext(
  consolidatedEnabled?: boolean,
  slugs?: readonly string[]
): boolean {
  const permissionSlugs = slugs ?? getCpContextPermissionSlugs();
  const consolidated = consolidatedEnabled ?? isConsolidatedPermissionsEnabled();

  if (consolidated) {
    const hostManage = readCpContextBoolean('CAN_MANAGE_COURSE_CATALOG');
    if (hostManage !== undefined) {
      return isEffectiveManageAtEmbedScope(hostManage);
    }
    if (permissionSlugs.length === 0) {
      return false;
    }
    if (!permissionSlugs.includes(PERMISSION_ALIAS_MANAGE_COURSE_CATALOG)) {
      return false;
    }
    if (isSchoolScopedByCpContext()) {
      return isSchoolIndividualCoursePlannerProductLevel();
    }
    return true;
  }

  if (permissionSlugs.length === 0) {
    return false;
  }

  const hasManageSlug =
    permissionSlugs.includes('admin_course_catalog') ||
    permissionSlugs.includes('admin_district_course_catalog');
  if (!hasManageSlug) {
    return false;
  }

  return !isSchoolUser();
}

/**
 * School branch of course-planner-ui `canViewEditCourseCatalog` (SCHOOL_INDIVIDUAL): requires both
 * `view_school_course_catalog` and `admin_course_catalog`.
 */
export function canViewEditSchoolCourseCatalogFromPermissionSlugs(
  permissions: readonly string[]
): boolean {
  return hasEveryPermissionSlug(permissions, [
    'view_school_course_catalog',
    'admin_course_catalog',
  ]);
}

/** True when Succeed embedded context includes a high school plus a district id (staff acting at a school within a district). */
function isSchoolScopedByCpContext(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.CP_CONTEXT?.HIGHSCHOOL_ID &&
    (!!window.CP_CONTEXT?.DISTRICT_ID || !!window.CP_CONTEXT?.HS_DISTRICT_ID)
  );
}

/**
 * Flag ON: district CP at an embedded school allows manage only for SCHOOL_INDIVIDUAL subscription;
 * district-subscribed schools are view-only at runtime (matches succeed embed booleans).
 */
function isEffectiveManageAtEmbedScope(hostManageGranted: boolean): boolean {
  if (!hostManageGranted) {
    return false;
  }
  if (isSchoolScopedByCpContext()) {
    return isSchoolIndividualCoursePlannerProductLevel();
  }
  return true;
}

/**
 * Returns true only when embedded with both `DISTRICT_ID` and `HIGHSCHOOL_ID` in `CP_CONTEXT`.
 * District-only users and standalone school contexts without `DISTRICT_ID` are false.
 * School users see read-only course catalog (e.g. no Actions column).
 *
 * When that school-scoped context is true and `CP_CONTEXT.USER_PERMISSIONS` is a **string[]** (slug list),
 * this also requires the school course-catalog permission pair from course-planner-ui
 * `canViewEditCourseCatalog` (school branch). Numeric `Record` permissions cannot be evaluated here;
 * in that case the school-scoped check alone applies (embed parity with hosts that do not pass slugs).
 *
 * **School-only subscription:** when GET `/user?product=7` reports `products['7'] === 'SCHOOL_INDIVIDUAL'`
 * (district does not have Course Planner; only the school does), this returns **false** so the school
 * behaves like district staff for Plan of study and course catalog UI (same as c-p-ui intent for that level).
 */
export function isSchoolUser(): boolean {
  if (!isSchoolScopedByCpContext()) {
    return false;
  }
  if (isSchoolIndividualCoursePlannerProductLevel()) {
    return false;
  }
  const raw = typeof window !== 'undefined' ? window.CP_CONTEXT?.USER_PERMISSIONS : undefined;
  if (Array.isArray(raw)) {
    const slugs = raw.filter((p): p is string => typeof p === 'string');
    return canViewEditSchoolCourseCatalogFromPermissionSlugs(slugs);
  }
  return true;
}

/**
 * True when course catalog operates at district scope (not school-scoped embed).
 * Used for district-only affordances such as course mapping export, which view-only
 * district users should access without manage permission.
 * School-only subscription (SCHOOL_INDIVIDUAL) is treated as district scope for UI parity.
 */
export function isDistrictCourseCatalogScope(): boolean {
  if (!isSchoolScopedByCpContext()) {
    return true;
  }
  return isSchoolIndividualCoursePlannerProductLevel();
}

/**
 * True when the course catalog export menu should include **Course mapping**.
 * Flag OFF: legacy parity with main (`!isSchoolUser()` / `isDistrictLevel`).
 * Flag ON: district scope only — view-only district users may export mapping without manage permission.
 */
export function canExportCourseMappingFromCpContext(): boolean {
  if (isConsolidatedPermissionsEnabled()) {
    return isDistrictCourseCatalogScope();
  }
  return !isSchoolUser();
}

/** NAVI-40132/40133/43588 — canonical view POS alias when consolidatedPermissions is ON. */
export const PERMISSION_ALIAS_VIEW_PLANS_OF_STUDY = 'view_school_plans_of_study' as const;

/** NAVI-40133 — canonical manage POS alias when consolidatedPermissions is ON. */
export const PERMISSION_ALIAS_MANAGE_PLANS_OF_STUDY = 'admin_course_planning' as const;

/** NAVI-40134 — canonical administer course planner alias when consolidatedPermissions is ON. */
export const PERMISSION_ALIAS_ADMINISTER_COURSE_PLANNER =
  'admin_school_course_history_settings' as const;

/**
 * NAVI-40132/40133/43588 — view plans of study (nav / read access).
 * Flag ON: host boolean from Succeed when embedded; else slug aliases
 * (view_school_plans_of_study or admin_course_planning; manage implies view).
 * Flag OFF: returns true (legacy callers use {@link isSchoolUser} via {@link isPosReadOnly}).
 */
export function canViewPlansOfStudyFromCpContext(
  consolidatedEnabled?: boolean,
  slugs?: readonly string[]
): boolean {
  const permissionSlugs = slugs ?? getCpContextPermissionSlugs();
  const consolidated = consolidatedEnabled ?? isConsolidatedPermissionsEnabled();

  if (!consolidated) {
    return true;
  }

  const hostView = readCpContextBoolean('CAN_VIEW_PLANS_OF_STUDY');
  if (hostView !== undefined) {
    return hostView;
  }
  if (permissionSlugs.length === 0) {
    return false;
  }
  return (
    permissionSlugs.includes(PERMISSION_ALIAS_VIEW_PLANS_OF_STUDY) ||
    permissionSlugs.includes(PERMISSION_ALIAS_MANAGE_PLANS_OF_STUDY)
  );
}

/**
 * NAVI-40133 — effective manage POS capability (mutations / manage UI).
 * Flag ON: host boolean from Succeed when embedded; else `admin_course_planning` slug.
 */
export function canManagePlansOfStudyFromCpContext(
  consolidatedEnabled?: boolean,
  slugs?: readonly string[]
): boolean {
  const permissionSlugs = slugs ?? getCpContextPermissionSlugs();
  const consolidated = consolidatedEnabled ?? isConsolidatedPermissionsEnabled();

  if (!consolidated) {
    return false;
  }

  const hostManage = readCpContextBoolean('CAN_MANAGE_PLANS_OF_STUDY');
  if (hostManage !== undefined) {
    return isEffectiveManageAtEmbedScope(hostManage);
  }
  if (permissionSlugs.length === 0) {
    return false;
  }
  if (!permissionSlugs.includes(PERMISSION_ALIAS_MANAGE_PLANS_OF_STUDY)) {
    return false;
  }
  if (isSchoolScopedByCpContext()) {
    return isSchoolIndividualCoursePlannerProductLevel();
  }
  return true;
}

/**
 * True when course catalog UI is read-only (no add/edit/delete/import).
 * CourseCatalog containers use `!isCourseCatalogReadOnly()` for district manage mode.
 *
 * Flag OFF: {@link isSchoolUser} (legacy embed parity).
 * Flag ON (NAVI-40131/40132): manage = `admin_course_catalog`, view = `view_courses_tab`
 * (manage implies view). School-scoped staff with manage stay read-only (legacy school pair).
 */
export function isCourseCatalogReadOnly(): boolean {
  if (isConsolidatedPermissionsEnabled()) {
    if (!canViewCourseCatalogFromCpContext(true)) {
      return true;
    }
    return !canManageCourseCatalogFromCpContext(true);
  }
  return isSchoolUser();
}

/**
 * True when plans of study / specializations UI is read-only (no add/edit/delete/publish).
 * PlanOfStudy and ClustersAndPathways containers use `!isPosReadOnly()` for manage mode.
 *
 * Flag OFF: {@link isSchoolUser} (legacy embed parity).
 * Flag ON (NAVI-40133/40132/43588): manage = `admin_course_planning`, view = `view_school_plans_of_study`
 * (manage implies view).
 */
export function isPosReadOnly(): boolean {
  if (isConsolidatedPermissionsEnabled()) {
    if (!canViewPlansOfStudyFromCpContext(true)) {
      return true;
    }
    return !canManagePlansOfStudyFromCpContext(true);
  }
  return isSchoolUser();
}

/**
 * NAVI-40134 — Course Planner configurations tab access.
 * Canonical alias: `admin_school_course_history_settings`
 * (display name: Administer course planner).
 *
 * Flag ON: `admin_school_course_history_settings` only (replaces bundled configuration aliases).
 * Flag OFF: legacy school POS manage aliases or district `edit_district_plan_of_study`
 * (pre-consolidation behavior; matches succeed menus).
 *
 * @param scope — `'district'` for district CP staff; default `'school'` (also used when embedded at a school).
 */
export function canAdministerCoursePlannerConfigurationsFromCpContext(
  consolidatedEnabled?: boolean,
  slugs?: readonly string[],
  scope: 'school' | 'district' = 'school'
): boolean {
  const consolidated = consolidatedEnabled ?? isConsolidatedPermissionsEnabled();

  if (consolidated) {
    const hostAdminister = readCpContextBoolean('CAN_ADMINISTER_COURSE_PLANNER');
    if (hostAdminister !== undefined) {
      // PHP already applied subscription-level checks (consolidatedManageGranted); trust the boolean
      // directly. isEffectiveManageAtEmbedScope is intentionally not used here: school-level
      // subscriptions (district has no CP) have DISTRICT_ID set in CP_CONTEXT, making them
      // school-scoped, which would incorrectly require SCHOOL_INDIVIDUAL product-level cache (NAVI-52849).
      return hostAdminister;
    }
    const permissionSlugs = slugs ?? getCpContextPermissionSlugs();
    if (permissionSlugs.length === 0) {
      return false;
    }
    if (!permissionSlugs.includes(PERMISSION_ALIAS_ADMINISTER_COURSE_PLANNER)) {
      return false;
    }
    if (isSchoolScopedByCpContext()) {
      return isSchoolIndividualCoursePlannerProductLevel();
    }
    return true;
  }

  const legacyGroups =
    scope === 'district'
      ? ([['edit_district_plan_of_study']] as const)
      : ([['admin_school_course_planning_settings'], ['admin_course_planning']] as const);

  return cpStaffPermissionAllowed({
    consolidatedEnabled: false,
    newAliases: [PERMISSION_ALIAS_ADMINISTER_COURSE_PLANNER],
    legacyGroups,
    slugs,
  });
}

/**
 * NAVI-40135 — recommend students for courses (recommendations tab).
 * May be granted to district or school users; Recommendations UI is school-level only.
 * Flag ON: `admin_school_course_recommendations` only (replaces bundled
 * `recommend_courses` and `view_courses_tab`).
 * Flag OFF: legacy OR of view recommendation aliases (pre-consolidation menu behavior).
 */
export function canRecommendStudentsForCoursesFromCpContext(
  consolidatedEnabled?: boolean,
  slugs?: readonly string[]
): boolean {
  return cpStaffPermissionAllowed({
    consolidatedEnabled: consolidatedEnabled ?? isConsolidatedPermissionsEnabled(),
    newAliases: ['admin_school_course_recommendations'],
    legacyGroups: [['view_recommended_courses'], ['view_school_course_recommendations']],
    slugs,
  });
}

export function isStandaloneSchoolUser(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.CP_CONTEXT?.HIGHSCHOOL_ID &&
    !window.CP_CONTEXT?.DISTRICT_ID &&
    !window.CP_CONTEXT?.HS_DISTRICT_ID
  );
}

/**
 * Returns the high school ID. Uses CP_CONTEXT.HIGHSCHOOL_ID when embedded, else VITE_HIGHSCHOOL_ID or fallback.
 * Needed for building direct course-planner-service paths in local mode.
 * Note: Does not fall back to HS_DISTRICT_ID (a district ID) to avoid incorrectly using a district ID
 * where a high school ID is expected in URLs.
 */
export function getHighschoolId(): string {
  if (typeof window !== 'undefined' && window.CP_STUDENT_CONTEXT?.HIGHSCHOOL_ID) {
    return window.CP_STUDENT_CONTEXT.HIGHSCHOOL_ID;
  }
  if (typeof window !== 'undefined' && window.CP_CONTEXT?.HIGHSCHOOL_ID) {
    return window.CP_CONTEXT.HIGHSCHOOL_ID;
  }
  const envId = import.meta.env.VITE_HIGHSCHOOL_ID;
  // '0' is the sentinel for "no specific school / district-wide catalog" in local dev.
  // Treat it the same as absent so student API headers carry a real school NID
  // (matches the guard in getStudentHighschoolId in student-api-paths.ts).
  if (envId && envId !== '0') {
    return envId;
  }
  return import.meta.env.VITE_STUDENT_HIGHSCHOOL_ID || '88881USPU';
}

/**
 * `naviance-highschool-id` for embedded staff requests (CPUI parity).
 * Legacy CPUI always sets this header: `CP_CONTEXT.HIGHSCHOOL_ID || ''`, so district staff send an empty string.
 * Omitting the header entirely (previous course-planner behavior) can yield 403 from succeed-api / edge rules.
 */
export function getEmbeddedNavianceHighschoolIdHeader(): string {
  if (typeof window === 'undefined' || !window.CP_CONTEXT) {
    return '';
  }
  const id = window.CP_CONTEXT.HIGHSCHOOL_ID;
  return typeof id === 'string' && id.length > 0 ? id : '';
}

/**
 * Trimmed `CP_CONTEXT.HIGHSCHOOL_ID` when Succeed injected it, else `undefined`.
 * Used to scope client-built payloads (e.g. course mapping export `{ schools }`) to the embedded school.
 * Unlike {@link getHighschoolId}, does **not** fall back to `VITE_HIGHSCHOOL_ID` — only real embed context.
 */
export function getCpContextHighschoolId(): string | undefined {
  if (typeof window === 'undefined' || !window.CP_CONTEXT) {
    return undefined;
  }
  const raw = window.CP_CONTEXT.HIGHSCHOOL_ID;
  if (typeof raw !== 'string') {
    return undefined;
  }
  const t = raw.trim();
  return t.length > 0 ? t : undefined;
}

/**
 * Returns raw Succeed permission IDs when embedded. Use when needed for permission checks.
 * Keys are permission IDs (e.g. "440", "1020"); values are typically 1.
 */
export function getSucceedPermissions(): Record<string, number> {
  if (typeof window !== 'undefined' && window.CP_CONTEXT?.USER_PERMISSIONS) {
    const raw = window.CP_CONTEXT.USER_PERMISSIONS;
    if (!Array.isArray(raw)) {
      return raw;
    }
  }
  return {};
}

/**
 * Returns true when embedded in Succeed and the user has the given Succeed permission.
 * Use for permission checks by Succeed permission ID (e.g. 440, "1020").
 */
export function hasSucceedPermission(permissionId: string | number): boolean {
  const perms = getSucceedPermissions();
  const key = String(permissionId);
  return key in perms && !!perms[key];
}

/**
 * Returns true when the current user should get district-style Plans of study controls (edit, delete, publish).
 * When false, school staff see read-only flows where applicable.
 * Uses {@link isPosReadOnly} plus `VITE_POS_SCHOOL_VIEW` for QA.
 */
export function isDistrictStaffForPos(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }
  if (import.meta.env.VITE_POS_SCHOOL_VIEW === 'true') {
    return false;
  }
  return !isPosReadOnly();
}

/** Minimal shape for tying cached ghost metadata to the embedded student (FC / Succeed). */
export type GhostEmbedSessionKeySource = { userId?: unknown; id?: unknown };

/**
 * FC / Course Planner sessions use `userId` for the folder student; `UserContext.UserSession` types `id`.
 * Ghost embed keys and impersonation checks need at least one of these.
 */
type StaffCoursePlanActorSession = UserSession & GhostEmbedSessionKeySource;

type CpContextWindow = Window & {
  CP_CONTEXT?: {
    GHOST_ACTOR_ROLE?: string;
    GHOST_ACTOR_USER_ID?: string;
  };
  CP_STUDENT_CONTEXT?: {
    GHOST_ACTOR_ROLE?: string;
    GHOST_ACTOR_USER_ID?: string;
  };
};

export const ROLES = {
  student: 'STUDENT',
  parent: 'PARENT',
  counselor: 'COUNSELOR',
  schoolUser: 'SCHOOL_USER',
  districUser: 'DISTRICT_USER',
  guest: 'GUEST',
  superUser: 'SUPER_USER',
  zoneUser: 'ZONE_USER',
};

export enum STUDENT_COURSE_PLAN_STATUS {
  IN_PROGRESS = 'In Progress',
  AWAITING_SCHOOL_APPROVAL = 'Awaiting School Approval',
  AWAITING_PARENT_APPROVAL = 'Awaiting Parent Approval',
  NOT_APPROVED_BY_PARENT = 'Not Approved By Parent',
  APPROVED = 'Approved',
}

let ghostActorEmbedCache: {
  studentKey: string;
  role: string;
  userId?: string;
} | null = null;

const isSchoolUserV2 = (role: string) =>
  [ROLES.counselor, ROLES.schoolUser, ROLES.districUser].includes(role);

const isSuperUser = (role: string) => ROLES.superUser === role;

export const isParent = (role: string) => ROLES.parent === role;

export const isNotParentOrGuest = (role: string, userSession?: UserSession) =>
  isStaff(role, userSession) || role === ROLES.student;

const isZoneUser = (role: string) => ROLES.zoneUser === role;
/** Always boolean: role-only checks used to return `undefined` for non-staff roles (falsy `user?.loginUser`). */
export const isStaff = (role: string, user?: UserSession): boolean =>
  Boolean(isSuperUser(role) || isSchoolUserV2(role) || isZoneUser(role) || user?.loginUser);

function embedStudentKey(session: GhostEmbedSessionKeySource | undefined): string {
  if (!session) {
    return '';
  }
  const id = session.userId ?? session.id;
  if (id === undefined || id === null || id === '') {
    return '';
  }
  return String(id);
}

const GHOST_ACTOR_STORAGE_PREFIX = 'cpuiGhostActor:v1:';

function persistGhostActorToStorage(studentKey: string, role: string, userId?: string): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(
      GHOST_ACTOR_STORAGE_PREFIX + studentKey,
      JSON.stringify({ role, userId })
    );
  } catch {
    // quota / private mode
  }
}

function rememberGhostFromWindow(session: GhostEmbedSessionKeySource | undefined): void {
  if (typeof window === 'undefined') {
    return;
  }
  const sk = embedStudentKey(session);
  if (!sk) {
    return;
  }
  const roleRaw = (window as CpContextWindow).CP_CONTEXT?.GHOST_ACTOR_ROLE;
  const role = typeof roleRaw === 'string' && roleRaw.length > 0 ? roleRaw : undefined;
  if (!role || !isStaff(role)) {
    return;
  }
  const idRaw = (window as CpContextWindow).CP_CONTEXT?.GHOST_ACTOR_USER_ID;
  const uid = typeof idRaw === 'string' && idRaw.length > 0 ? idRaw : undefined;
  ghostActorEmbedCache = { studentKey: sk, role, userId: uid };
  persistGhostActorToStorage(sk, role, uid);
}

function readGhostActorPayloadFromStorage(
  studentKey: string
): { role: string; userId?: string } | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(GHOST_ACTOR_STORAGE_PREFIX + studentKey);
    if (!raw) {
      return null;
    }
    const o = JSON.parse(raw) as { role?: string; userId?: string };
    if (o?.role && isStaff(o.role)) {
      return { role: o.role, userId: o.userId };
    }
  } catch {
    return null;
  }
  return null;
}

/** Session-like shape used for effective-role resolution (UI and API). */
export type SessionLike =
  | { role?: string | null; loginUser?: { role?: string } | null }
  | null
  | undefined;

/**
 * Succeed student-folder page sets these when LD `feature.coursePlanner.ManageandViewPermissionsUpdate` is on
 * so CPUI can correct synthetic `loginUser.role` (JWT student session uses role STUDENT).
 *
 * When `session` is passed, staff ghost metadata is cached per embedded student so FC SPA re-entries
 * (e.g. second "eye" navigation) still resolve the actor if `window.CP_CONTEXT` was only set on full page load.
 */
export function readGhostActorRoleFromContext(
  session?: GhostEmbedSessionKeySource
): string | undefined {
  if (typeof window !== 'undefined') {
    const roleRaw = (window as CpContextWindow).CP_CONTEXT?.GHOST_ACTOR_ROLE;
    const fromWindow = typeof roleRaw === 'string' && roleRaw.length > 0 ? roleRaw : undefined;
    if (fromWindow && isStaff(fromWindow)) {
      rememberGhostFromWindow(session);
      return fromWindow;
    }

    const studentRoleRaw = (window as CpContextWindow).CP_STUDENT_CONTEXT?.GHOST_ACTOR_ROLE;
    const fromStudentContext =
      typeof studentRoleRaw === 'string' && studentRoleRaw.length > 0 ? studentRoleRaw : undefined;
    if (fromStudentContext && isStaff(fromStudentContext)) {
      const sk = embedStudentKey(session);
      if (sk) {
        const studentIdRaw = (window as CpContextWindow).CP_STUDENT_CONTEXT?.GHOST_ACTOR_USER_ID;
        const studentUid =
          typeof studentIdRaw === 'string' && studentIdRaw.length > 0 ? studentIdRaw : undefined;
        ghostActorEmbedCache = { studentKey: sk, role: fromStudentContext, userId: studentUid };
        persistGhostActorToStorage(sk, fromStudentContext, studentUid);
      }
      return fromStudentContext;
    }
  }
  const sk = embedStudentKey(session);
  if (!sk) {
    return undefined;
  }
  if (ghostActorEmbedCache?.studentKey === sk && isStaff(ghostActorEmbedCache.role)) {
    return ghostActorEmbedCache.role;
  }
  const stored = readGhostActorPayloadFromStorage(sk);
  if (stored) {
    ghostActorEmbedCache = {
      studentKey: sk,
      role: stored.role,
      userId: stored.userId,
    };
    return stored.role;
  }
  return undefined;
}

/**
 * Effective actor role: when ghosted/impersonated, loginUser is the staff viewing as student;
 * otherwise the main session user. Use for approval UI gating and API userType so logic cannot drift.
 */
export const getEffectiveRoleFromSession = (session: SessionLike): string | null =>
  session?.loginUser?.role ?? session?.role ?? null;

/**
 * `role === STUDENT` plus staff School-Approval context: staff user id on `loginUser` or ghost actor
 * from embed/storage. Direct students also get legacy `view_and_edit_course_plans` (and similar) on
 * expanded ACL — that alone must not count as staff.
 */
function studentRoleSessionHasStaffCoursePlanActor(user: StaffCoursePlanActorSession): boolean {
  if (user.coursePlannerStaffInStudentFolder === true) {
    return true;
  }
  if (user.coursePlannerStaffInStudentFolder === false) {
    return false;
  }
  const effectiveRole = getEffectiveRoleFromSession(user);
  if (effectiveRole && isStaff(effectiveRole)) {
    return true;
  }
  const sessionKey = user.userId ?? user.id;
  const sessionUid = Number(sessionKey);
  const actorUid =
    user.loginUser?.userId !== undefined && user.loginUser?.userId !== null
      ? Number(user.loginUser.userId)
      : NaN;
  if (!Number.isNaN(sessionUid) && !Number.isNaN(actorUid) && actorUid !== sessionUid) {
    return true;
  }
  const ghostRole = readGhostActorRoleFromContext(user);
  return Boolean(ghostRole && isStaff(ghostRole));
}

export function sessionHasStaffCoursePlanPermissionAliases(
  user: StaffCoursePlanActorSession | null | undefined,
  useNewPermissionsModel: boolean
): boolean {
  if (!useNewPermissionsModel || !user) {
    return false;
  }
  const aliases = getCoursePlanPermissionAliases(user);
  const hasManage = aliases.includes(PERMISSION_ALIAS_MANAGE_COURSE_PLANS);
  const hasView = aliases.includes(PERMISSION_ALIAS_VIEW_COURSE_PLANS);
  // view_and_edit_course_plans (legacy combined alias) does not apply to ghosted staff sessions.
  if (hasManage || hasView) {
    if (!isStudent(user)) {
      return true;
    }
    return studentRoleSessionHasStaffCoursePlanActor(user);
  }
  return false;
}

/**
 * Role used to decide whether the awaiting-approval status bar is staff-suppressed.
 * Real students stay STUDENT unless `CP_CONTEXT.GHOST_ACTOR_ROLE` identifies staff (ghost embed).
 */
export function resolveActorRoleForPlanStatusBar(
  session: UserSession | undefined,
  userRoleProp: string,
  enableCustomStaffCheck: boolean,
  useNewPermissions: boolean
): string {
  const useSessionRole = Boolean(session) && (enableCustomStaffCheck || useNewPermissions);
  if (!useSessionRole || !session) {
    return userRoleProp;
  }
  const role = getEffectiveRoleFromSession(session) ?? userRoleProp;
  if (!useNewPermissions) {
    return role;
  }
  if (isStaff(role)) {
    return role;
  }
  if (session.role === ROLES.student || session.role === 'STUDENT') {
    const ghostRole = readGhostActorRoleFromContext(session);
    if (ghostRole && isStaff(ghostRole)) {
      return ghostRole;
    }
  }
  return role;
}

/**
 * Single place for course-plan edit vs view-only.
 * View-only when: flag ON + `loginUser` aliases include `view_student_course_plans` and not manage
 * (matches BRAPI canonical ghost merge output in `resources.courses`).
 *
 * @param options.viewOnlyOverride - When set, use for viewOnly (e.g. from parent prop).
 */
export interface CoursePlanEditStateOptions {
  useNewPermissions: boolean;
  canEditPlanForUser: boolean;
  viewOnlyOverride?: boolean;
}

export function getCoursePlanEditState(
  user: UserSession | null | undefined,
  options: CoursePlanEditStateOptions
): { canEdit: boolean; viewOnly: boolean } {
  const { useNewPermissions, canEditPlanForUser, viewOnlyOverride } = options;

  if (!canEditPlanForUser) {
    return { canEdit: true, viewOnly: false };
  }
  if (!useNewPermissions) {
    const canEdit = canEditPlan(user, false);
    return { canEdit, viewOnly: false };
  }

  const canEditFromSession = canEditPlan(user, useNewPermissions);
  const aliases = getCoursePlanPermissionAliases(user);
  const hasView =
    aliases.includes(PERMISSION_ALIAS_VIEW_COURSE_PLANS) ||
    hasLegacyNumericCoursePermission(
      getLoginUserCoursesRaw(user),
      LEGACY_NUMERIC_VIEW_COURSE_PLANS
    );
  const viewOnly = !canEditFromSession && hasView;

  // Override only restricts (force view-only); it never grants edit. canEdit still respects session.
  if (viewOnlyOverride === true) {
    return { canEdit: false, viewOnly: true };
  }
  return { canEdit: canEditFromSession, viewOnly };
}
export const isFeatureFlagAllowed = (featureFlags: Record<string, unknown>, featureFlag: string) =>
  featureFlags?.[featureFlag] === undefined || featureFlags?.[featureFlag] === true;

/** LD flag gating manage-vs-view course plan permission enforcement (see {@link getCoursePlanEditState}). */
export const MANAGE_AND_VIEW_PERMISSIONS_UPDATE_LD_KEY =
  'feature.coursePlanner.ManageandViewPermissionsUpdate' as const;

/**
 * Staff SCPM "View student plan" (eye icon) canEdit resolution — mirrors legacy course-planner-ui's
 * `getCanEditForCoursePlansPage` exactly: flag off/unset -> true; flag on -> host
 * `CP_CONTEXT.CAN_MANAGE_COURSE_PLANS` boolean when present, else true.
 * @returns Whether the current staff session can manage (edit) course plans, vs. view-only.
 */
export function getStaffCanManageCoursePlans(): boolean {
  if (
    readLdBoolean(getCoursePlannerFeatureMap(), MANAGE_AND_VIEW_PERMISSIONS_UPDATE_LD_KEY) !== true
  ) {
    return true;
  }
  const fromContext = readCpContextBoolean('CAN_MANAGE_COURSE_PLANS');
  return typeof fromContext === 'boolean' ? fromContext : true;
}

export function canManageStudentCoursePlansFromCpContext(
  user: UserSession | null | undefined = getLegacyContextUserSession()
): boolean {
  const isSucceedSuperUser =
    typeof window !== 'undefined' &&
    (window.CP_CONTEXT?.SUPER_USER === '1' || window.CP_CONTEXT?.SUPER_USER === 'true');
  if (isSucceedSuperUser) {
    return true;
  }
  if (
    isStudentCoursePlanManagerEnabled(true) &&
    getEffectiveRoleFromSession(user) === AclRoles.SUPER_USER
  ) {
    return true;
  }

  const fromContext = readCpContextBoolean('CAN_MANAGE_COURSE_PLANS');
  if (typeof fromContext === 'boolean') {
    return fromContext;
  }

  const permissions = user?.loginUser ? user.loginUser.permissions : user?.permissions;
  const courses =
    permissions && typeof permissions === 'object' && 'resources' in permissions
      ? (permissions as { resources?: { courses?: unknown } }).resources?.courses
      : undefined;
  return (
    getCoursePlanPermissionAliases(user).includes(PERMISSION_ALIAS_MANAGE_COURSE_PLANS) ||
    hasLegacyNumericCoursePermission(courses, LEGACY_NUMERIC_MANAGE_COURSE_PLANS) ||
    ((user === null || user === undefined) && typeof fromContext !== 'boolean')
  );
}

/**
 * Builds the eye-icon "View student plan" destination for the staff SCPM page, matching legacy
 * course-planner-ui's `StudentCoursePlansTable.handleActionClick` (`ACTIONS_TYPE.DETAIL`): open the
 * host's per-student URL with a `redirectUrl` back into the course-planner-next manage-course-plan
 * route, carrying `staffEditMode` so the destination renders Manage vs. View accordingly. Chooses
 * `&` over `?` and URL-encodes the nested redirect when the host URL already has a query string
 * (matches `apps/student-folder-mfe/src/pages/student-folder-plans-page.tsx`'s `getPlanEditorPath`).
 * @param studentId - Target student's id, substituted into the host `STUDENT_URL` `{student_id}` placeholder.
 * @param planId - Course plan id to open on the destination manage-course-plan route.
 * @returns The host redirect URL, or `undefined` when not embedded in Succeed (no host `STUDENT_URL`
 * template) or the row has no `studentId`, so callers can fall back to an in-app relative link.
 */
export function getStaffStudentCoursePlanViewUrl(
  studentId: string | undefined,
  planId: string
): string | undefined {
  const studentUrlTemplate =
    typeof window !== 'undefined' ? window.CP_CONTEXT?.STUDENT_URL : undefined;
  if (!studentUrlTemplate || !studentId) {
    return undefined;
  }
  const host = studentUrlTemplate.replace('{student_id}', studentId);
  const canEdit = getStaffCanManageCoursePlans();
  const redirectUrl = `/course-planner-next/plans/manage-course-plan/${planId}?staffEditMode=${canEdit}`;
  const querySeparator = host.includes('?') ? '&' : '?';
  return `${host}${querySeparator}redirectUrl=${encodeURIComponent(redirectUrl)}`;
}

/**
 * Resolves the host user session used for course-plan edit/view permission checks (NAVI-45640).
 * Mirrors FCUI's `CP_STUDENT_CONTEXT.USER_SESSION` envelope, falling back to the legacy
 * `window.context.userSession` shape used by standalone/local embeds.
 */
export function getLegacyContextUserSession(): UserSession | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const hostSession = window.CP_STUDENT_CONTEXT?.USER_SESSION?.userSession;
  if (hostSession && typeof hostSession === 'object') {
    return hostSession as UserSession;
  }
  return (window as unknown as { context?: { userSession?: UserSession } }).context?.userSession;
}

/** Course-plan edit/view access resolved from the host session + feature flags (NAVI-45640). */
export interface CoursePlanAccessState {
  canEdit: boolean;
  viewOnly: boolean;
  /** False only when the user has neither Manage nor View course plan permission. */
  hasAnyAccess: boolean;
}

/**
 * Single entry point for "can this user see/use Manage Course Plans at all" (NAVI-45640 AC1).
 * Real (non-ghosted) student and demo sessions always resolve `canEdit: true` via
 * {@link canEditPlan}'s student fallback, so this never blocks students from their own plans —
 * it only restricts staff sessions ghosting/viewing a student without course-plan permission.
 * When no host session is resolvable at all (local/standalone/test runs without an embed context),
 * this is permissive (matches other permission gates' local/dev behavior in this codebase).
 *
 * Direct student login additionally requires both the base `course_planner_next` courses alias and
 * the legacy `view_and_edit_course_plans` alias: when the session is present but either is missing —
 * including when the host attached no `permissions` payload at all (student manually navigates to a
 * Manage Course Plans URL, or the permission attach failed) — access is denied outright. No other
 * courses alias (view/manage) is required or checked for a real student's own plans. This requirement
 * does not apply to ghosted staff sessions.
 */
export function getCoursePlanAccessState(
  user: UserSession | null | undefined = getLegacyContextUserSession()
): CoursePlanAccessState {
  if (!user) {
    return { canEdit: true, viewOnly: false, hasAnyAccess: true };
  }

  if (isStudent(user) && !isGhosted(user) && !isDemo(user)) {
    const directStudentAliases = getCoursePlanPermissionAliases(user);
    const hasDirectStudentAccess =
      directStudentAliases.includes(PERMISSION_ALIAS_COURSE_PLANNER_NEXT) &&
      directStudentAliases.includes(PERMISSION_ALIAS_VIEW_AND_EDIT_COURSE_PLANS);
    if (!hasDirectStudentAccess) {
      return { canEdit: false, viewOnly: false, hasAnyAccess: false };
    }
  }

  const featureFlags = getCoursePlannerFeatureMap() ?? {};
  const canEditPlanForUser = isFeatureFlagAllowed(
    featureFlags,
    'featureCoursePlannerStudentCoursePlanCourseAddEditPermissions'
  );
  const useNewPermissions = isFeatureFlagAllowed(
    featureFlags,
    MANAGE_AND_VIEW_PERMISSIONS_UPDATE_LD_KEY
  );
  const { canEdit, viewOnly } = getCoursePlanEditState(user, {
    useNewPermissions,
    canEditPlanForUser,
  });
  const hasAnyAccess = canEdit || viewOnly;
  const isGuardianView =
    isStudentCoursePlanManagerEnabled(true) &&
    (user.role === AclRoles.PARENT ||
      getEffectiveRoleFromSession(user) === AclRoles.PARENT ||
      Boolean(user.parent) ||
      Boolean(user.staffUser));
  return {
    canEdit: canEdit && !isGuardianView,
    viewOnly: isGuardianView ? hasAnyAccess : viewOnly,
    hasAnyAccess,
  };
}
